/**
 * formatTarget — Walter 2026-06-24. Tracks the currently-focused list editor so
 * the global header Format menu knows which list + caret line to act on, AND
 * tracks that caret line's current format state so the buttons can show as
 * ACTIVE (pressed) when the line already has that formatting — and toggle it
 * OFF on a second click, like a real formatting toolbar.
 *
 * Editors register on focus with {listId, textareaRef | cmRef, commit,
 * isReminderList, readOnly, notifyContentChange}. While focused we watch
 * `selectionchange` + `input` to keep `line` (the caret line's {color,
 * headerLevel, done, checkbox, bell, log}) in sync. The menu reads `focused`
 * (enabled) and `line` (active buttons).
 *
 * CM6 support (Walter 2026-07-16, format-menu refresh): a target may register
 * with `cmRef` (the MarkdownEditor ref) instead of `textareaRef`. Value +
 * selection are then read from the CM6 view, and the panel-provided `commit`
 * dispatches the change to the view (see the panels). ⚠️ Recurring gotcha
 * (ledger #134–137): `textareaRef` is NULL in CM6 mode — every focus check here
 * goes through the target accessor, never document.activeElement===textarea
 * alone.
 *
 * notifyContentChange lets a Format action that adds/removes a 🔔 reconcile the
 * reminder doc (create/delete) through the editor's capture engine — the plain
 * commit path alone wouldn't. (CM6 targets OMIT it: their commit dispatches to
 * the view, whose onChange already runs the capture reconciler — wiring both
 * would double-reconcile.)
 */

import { useSyncExternalStore } from 'react';
import {
  isDoneLine, setLineFormat, removeReminderMarker,
  parseHighlightMarker, parseCheckbox, CHECKBOX_MARKER,
  setLinesHighlight, setLineDone, wrapSelectionHighlight,
} from './highlightMarkers';
import { formatLogDate } from './reminderParser';
import { toggleInlineWrap } from './inlineEmphasis';
import { paragraphIfNoSelection } from './paragraphRange';
import { moveLinesUp, moveLinesDown } from './moveLines';

const BELL_RE = /^(?:🔔|\[DONE\]|🔕|~~🔔)/;
const HEADER_RE = /^\s*(#{1,6})\s/;
const LOG_RE = /^@\d{4}-\d{2}-\d{2}/;
const EMPTY_LINE = { color: 0, headerLevel: 0, done: false, checkbox: false, bell: false, log: false };

// state.target = { listId, textareaRef?, cmRef?, commit, isReminderList,
//                  readOnly, notifyContentChange? } | null
let state = { target: null, focused: false, line: EMPTY_LINE };
const listeners = new Set();
function emit() { for (const l of listeners) l(); }

// ── editor accessor — the ONE place that knows both editor shapes ────────────
// Returns { value, selStart, selEnd, hasFocus } or null when the target's
// editor isn't mounted. CM6 first: in live-preview mode textareaRef is null.
function readEditor(target) {
  const view = target && target.cmRef && target.cmRef.current && target.cmRef.current.view;
  if (view) {
    const sel = view.state.selection.main;
    return {
      value: view.state.doc.toString(),
      selStart: sel.from,
      selEnd: sel.to,
      // view.hasFocus is false whenever document.hasFocus() is (e.g. DevTools
      // focused, automation) — also accept "the contentDOM is the active
      // element" so line-state tracking doesn't stall in those windows.
      hasFocus: view.hasFocus
        || (typeof document !== 'undefined' && document.activeElement === view.contentDOM),
    };
  }
  const ta = target && target.textareaRef && target.textareaRef.current;
  if (ta) {
    return {
      value: ta.value || '',
      selStart: ta.selectionStart,
      selEnd: ta.selectionEnd,
      hasFocus: typeof document !== 'undefined' && document.activeElement === ta,
    };
  }
  return null;
}

function readLineState(value, caret) {
  const v = value || '';
  const lineStart = v.lastIndexOf('\n', caret - 1) + 1;
  let lineEnd = v.indexOf('\n', caret);
  if (lineEnd === -1) lineEnd = v.length;
  const line = v.slice(lineStart, lineEnd);
  const hl = parseHighlightMarker(line);
  const hm = line.match(HEADER_RE);
  return {
    color: hl ? hl.colorIndex : 0,
    headerLevel: hm ? hm[1].length : 0,
    done: isDoneLine(line),
    checkbox: !!parseCheckbox(line),
    bell: BELL_RE.test(line),
    log: LOG_RE.test(line),
  };
}

function sameLine(a, b) {
  return a.color === b.color && a.headerLevel === b.headerLevel
    && a.done === b.done && a.checkbox === b.checkbox && a.bell === b.bell && a.log === b.log;
}

let detach = null;
function refreshLineState() {
  const ed = readEditor(state.target);
  if (!ed) return;
  const next = readLineState(ed.value, ed.selStart);
  if (!sameLine(next, state.line)) {
    state = { ...state, line: next };
    emit();
  }
}

// Format keyboard shortcuts that work on BOTH editors via one document-level
// listener (attached per target): Ctrl+Alt+1/2/3 → heading level (Walter
// 2026-07-16, Docs/Word convention) and Alt+Shift+S → strikethrough (Walter's
// muscle memory; no cross-app standard — Docs uses Alt+Shift+5, Notion
// Ctrl+Shift+S). The actions themselves toggle.
function onFormatShortcut(e) {
  if (!state.focused || !state.target || state.target.readOnly) return;
  if (e.ctrlKey && e.altKey && !e.shiftKey && !e.metaKey
      && (e.key === '1' || e.key === '2' || e.key === '3')) {
    e.preventDefault();
    formatActions.header(Number(e.key));
    return;
  }
  if (e.altKey && e.shiftKey && !e.ctrlKey && !e.metaKey && e.code === 'KeyS') {
    e.preventDefault();
    formatActions.strike();
  }
}

export function setFormatTarget(payload) {
  if (detach) { detach(); detach = null; }
  state = { target: payload, focused: true, line: EMPTY_LINE };
  if (typeof document !== 'undefined' && readEditor(payload)) {
    // `selectionchange` fires for the textarea AND for CM6's contenteditable;
    // only refresh while the target editor actually has focus so a selection
    // elsewhere on the page doesn't repaint the menu.
    const onSel = () => {
      const ed = readEditor(state.target);
      if (ed && ed.hasFocus) refreshLineState();
    };
    document.addEventListener('selectionchange', onSel);
    document.addEventListener('keydown', onFormatShortcut, true);
    // Keystroke edits: the textarea emits `input`; CM6's contentDOM does too.
    const inputEl = payload.cmRef
      ? (payload.cmRef.current && payload.cmRef.current.view && payload.cmRef.current.view.contentDOM)
      : (payload.textareaRef && payload.textareaRef.current);
    const onInput = () => refreshLineState();
    if (inputEl) inputEl.addEventListener('input', onInput);
    detach = () => {
      document.removeEventListener('selectionchange', onSel);
      document.removeEventListener('keydown', onFormatShortcut, true);
      if (inputEl) inputEl.removeEventListener('input', onInput);
    };
    refreshLineState();
  }
  emit();
}

export function blurFormatTarget(listId) {
  if (state.target && state.target.listId === listId && state.focused) {
    state = { ...state, focused: false };
    emit();
  }
}

function getSnapshot() { return state; }
function subscribe(cb) { listeners.add(cb); return () => listeners.delete(cb); }

export function useFormatTarget() {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

// Run a pure line-transform against the live target, commit, and keep the
// reminder engine + active-state in sync.
function applyToTarget(fn) {
  const { target } = state;
  if (!target || target.readOnly) return;
  const ed = readEditor(target);
  const content = ed ? ed.value : '';
  const start = ed ? ed.selStart : content.length;
  const end = ed ? ed.selEnd : start;
  const hadBell = state.line.bell;
  const res = fn(content, start, end, { isReminderList: target.isReminderList });
  if (res && res.changed) {
    target.commit(res.nextValue, res.nextSelStart, res.nextSelEnd);
    target.notifyContentChange?.(content, res.nextValue);
    if (ed && typeof requestAnimationFrame === 'function') requestAnimationFrame(refreshLineState);
    // Switching a reminder line to any other format (or toggling the bell off)
    // DELETES the reminder — warn with an undo toast that restores it. Walter
    // 2026-06-24. The notifyContentChange above already reconciled the doc.
    if (hadBell && !readLineState(res.nextValue, res.nextSelStart).bell) {
      target.pushToast?.('reminder removed', () => {
        target.commit(content, start, end);
        target.notifyContentChange?.(res.nextValue, content);
        if (typeof requestAnimationFrame === 'function') requestAnimationFrame(refreshLineState);
      }, { ttlMs: 6000 });
    }
  }
  return res;
}

// The actions. Line formats are exclusive + toggle: clicking a format SWITCHES
// the line to it (replacing whatever it was), so a button is never a no-op;
// clicking the format the line ALREADY has toggles back to plain. Inline
// emphasis (bold/italic) wraps/unwraps the SELECTION instead — mirroring the
// CM6 Ctrl+B/I semantics — and stacks with any line format. `state.line` (the
// live caret-line state) drives both the toggle decision and the active look.
export const formatActions = {
  // Walter 2026-08-22: WITH A SELECTION the colour buttons wrap it — `^word^`,
  // exactly the way bold wraps a selection — so a span can be applied without
  // typing carets by hand, which matters most on the phone. With no selection
  // (or a multi-line one, where a span has no sensible rendering) they keep
  // marking the whole paragraph, unchanged.
  highlight: (colorIndex) => applyToTarget((c, s, e, o) => {
    const wrapped = wrapSelectionHighlight(c, s, e, colorIndex);
    if (wrapped) return wrapped;
    return setLineFormat(c, s, e, state.line.color === colorIndex ? '' : '^'.repeat(colorIndex), o);
  }),
  header: (level) => applyToTarget((c, s, e, o) =>
    setLineFormat(c, s, e, state.line.headerLevel === level ? '' : `${'#'.repeat(level)} `, o)),
  done: () => applyToTarget((c, s, e, o) =>
    setLineFormat(c, s, e, state.line.done ? '' : '[x]', o)),
  // GFM empty checkbox `- [ ] ` (Walter 2026-07-16). Renders as a clickable
  // box in CM6; raw in the textarea (same as the other markers).
  checkbox: () => applyToTarget((c, s, e, o) =>
    setLineFormat(c, s, e, state.line.checkbox ? '' : CHECKBOX_MARKER, o)),
  reminder: () => applyToTarget((c, s, e, o) =>
    (state.line.bell ? removeReminderMarker(c, s, e) : setLineFormat(c, s, e, '🔔 ', o))),
  // Log = prepend today's @date stamp (same convention as designated log
  // lists), so the line shows in the Logs panel. Exclusive + toggles like the
  // others. Walter 2026-06-24 ("prepend, that's how we've done it elsewhere").
  log: () => applyToTarget((c, s, e, o) =>
    setLineFormat(c, s, e, state.line.log ? '' : `@${formatLogDate(new Date())} `, o)),
  // Inline emphasis — selection-scoped, NOT a line format (Walter 2026-07-16).
  // Same three-branch toggle as the CM6 Mod-B/I keymap (lib/inlineEmphasis).
  // Strike added on Walter's review round 2 ("I use it a lot myself").
  // Walter 2026-08-22: every format button now reads the same — SELECTION →
  // that text; NO selection → the whole paragraph. Previously highlight marked
  // the paragraph while bold/italic/strike inserted an empty `****` pair and
  // parked the caret inside, so the toolbar disagreed with itself. His words:
  // "bold, italics, and strikethrough now behave differently from highlights".
  //
  // Fixed at the BUTTON level rather than by inventing one-sided `**` syntax:
  // a line starting with `*` is a bullet in everyone's muscle memory, and
  // `* buy milk` silently becoming italic is a trap. Wrapping the paragraph
  // needs no new syntax — `**a whole line**` is already valid and already
  // renders.
  //
  // The empty-line case KEEPS the old behaviour: with nothing to wrap, an
  // empty pair with the caret inside is the useful thing ("start typing bold").
  bold: () => applyToTarget((c, s, e) => toggleInlineWrap(c, ...paragraphIfNoSelection(c, s, e), '**')),
  italic: () => applyToTarget((c, s, e) => toggleInlineWrap(c, ...paragraphIfNoSelection(c, s, e), '*')),
  strike: () => applyToTarget((c, s, e) => toggleInlineWrap(c, ...paragraphIfNoSelection(c, s, e), '~~')),
  // Move the caret line (or selected block) up/down one row — the same
  // transform bound to Alt+Shift+↑/↓ in the editors, surfaced as a Format
  // menu button. Walter 2026-07-02. moveLines returns null at a boundary,
  // which applyToTarget treats as a no-op (no `changed`).
  moveLine: (dir) => applyToTarget((c, s, e) => {
    const r = (dir === 'up' ? moveLinesUp : moveLinesDown)(c, s, e);
    return r ? { changed: true, ...r } : null;
  }),
  // Keyboard shortcuts, CM6 path (Walter 2026-07-19: Ctrl+H was dead in the
  // live-preview editor — useLineHighlight listens on the textarea). These
  // mirror the TEXTAREA hook's semantics (setLinesHighlight / setLineDone:
  // headers + bells are SKIPPED), not the menu buttons' exclusive-switch.
  cycleHighlight: () => applyToTarget((c, s, e, o) =>
    setLinesHighlight(c, s, e, (state.line.color + 1) % 4, o)),
  toggleDone: () => applyToTarget((c, s, e, o) =>
    setLineDone(c, s, e, !state.line.done, o)),
};
